import {
    Component, EventEmitter, Input, OnInit, Output
} from '@angular/core';
import {
    selectActualVideoState,
    selectCurrentFrameFromState,
    selectCurrentTimeFromState,
    selectTotalTimeFromState
} from '../../../shared/stores/root-store/selectors/video.selector';
import {Store} from '@ngrx/store';
import VideoState from '../../../shared/stores/root-store/video.state';
import {
    ChangeSpaceKeyListenerAction,
    SetVideoStateTypeAction,
    UpdateNavigateTimeAction
} from '../../../shared/stores/root-store/actions/video.action';
import {VideoStateEnum} from '../../../../enums/video-state.enum';
import {OverlayType} from '../../../../enums/overlay-type.enum';
import {OverlayService} from '../../../shared/services/overlay.service';
import {combineLatest} from 'rxjs';
import {tap} from 'rxjs/operators';
import OverlayModel from '../../../../models/overlay.model';
import {EventTrackingService} from '../../../shared/services/event-tracking.service';
import {EventEnum} from '../../../../enums/event.enum';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {SessionService} from '../../../shared/services/session.service';
import {BrowserService} from '../../../shared/services/browser.service';
import {EventEmitterService} from '../../../shared/services/event-emitter.service';
import {PlayerEventApiEnum} from '../../../../enums/playerEventApi.enum';
import {SendAction} from '../../../shared/stores/root-store/actions/external-actions.action';

@Component({
    selector: 'eocm-field',
    templateUrl: './field.component.html',
    styleUrls: [
        './field.component.scss',
        '../../overlay.component.scss',
        '../button-overlay/button-overlay.component.scss'
    ]
})
export class FieldComponent implements OnInit {
    @Input() field: OverlayModel;
    @Input() formsShown: boolean;
    @Input() formGroup: UntypedFormGroup;
    @Input() isValidationErrorDisplayed: boolean;
    @Output() formsShownChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() submitButtonClicked: EventEmitter<boolean> = new EventEmitter<boolean>();

    opacity = 0;
    isVisible = false;
    style;
    currentTime: number;
    position = {
        x: 0, y: 0
    };
    isChecked = false;
    isSubmitted = false;
    isHoverState = false;

    private videoState;
    private formStopTime = -1;

    private readonly PORTRAIT_ORIENTATION: OrientationType = 'portrait-primary';
    private readonly LANDSCAPE_ORIENTATION: OrientationType = 'landscape-primary';

    constructor(
        private store: Store<{ video: VideoState }>,
        private overlayService: OverlayService,
        private tracking: EventTrackingService,
        private formBuilder: UntypedFormBuilder,
        private sessionService: SessionService,
        private browserService: BrowserService,
        private eventEmitterService: EventEmitterService
    ) {
    }

    ngOnInit() {
        const store = this.store;
        combineLatest([
            store.select(selectActualVideoState),
            store.select(selectCurrentTimeFromState),
            store.select(selectCurrentFrameFromState),
            store.select(selectTotalTimeFromState)
        ]
        ).pipe(tap(([videoState, currentVideoTime, currentVideoFrame, totalTime]) => {
            this.videoState = videoState;
            this.currentTime = currentVideoTime;
            if (this.field.time.end === totalTime) {
                this.field.time.end += 0.5;
            }
            this.isVisible = this.overlayService.isDisplayed(currentVideoTime, this.field);

            if (this.isSubmitted && !this.isVisible) {
                this.isSubmitted = false;
            }

            if (this.field.transition?.frames) {
                this.opacity = this.overlayService.getCurrentOpacity(currentVideoFrame, this.field);
            }

            if (this.isVisible) {
                this.position = this.overlayService.getCurrentPosition(currentVideoFrame, this.field);
            }
            this.stopVideoIfFormShown();
        })).subscribe();

        this.style = this.overlayService.getStyle(this.field, this.position);
        this.initializeForms();
    }

    toggleHoverState(): void  {
        if (!this.browserService.isMobileDevice()) {
            this.isHoverState = !this.isHoverState;
        }
    }

    getStyle(isHover: boolean = false): any {
        return this.overlayService.getStyle(this.field, this.position, isHover, this.isVisible);
    }

    onSubmit(): void {
        this.submitButtonClicked.emit(true);
        const fieldData = {
        };
        if (this.formGroup.valid) {
            this.isSubmitted = true;
            this.store.dispatch(SetVideoStateTypeAction({
                videoState: VideoStateEnum.Playing
            }));

            this.skipAfterFormStopTime();

            Object.keys(this.formGroup.value).forEach((key) => {
                fieldData[key] = this.formGroup.value[key];
                this.sessionService.data.set(key, fieldData[key]);
            });

            this.tracking.track(EventEnum.FormSubmited, fieldData);
            this.tracking.track(EventEnum.ButtonClicked, {
                reportingId: this.field.reportingId,
                buttonValue: 'clicked'
            });
            this.sessionService.sessionUpdated.next(undefined);
            this.sessionService.save()
                .then(() => {
                    this.field.actions?.forEach(actionId => {
                        this.store.dispatch(SendAction({
                            actionId
                        }))
                    });
                });
            this.eventEmitterService.emitEvent(PlayerEventApiEnum.FormSubmitted, fieldData);
        }
    }

    private skipAfterFormStopTime() {
        if (!this.formsShown) {
            this.formsShown = true;
            this.store.dispatch(UpdateNavigateTimeAction({
                navigationTime: this.field.time.end + this.formStopTime
            }));
        }
    }

    private stopVideoIfFormShown(): void {
        const field = this.field;

        if (this.currentTime <= this.field.time.start) {
            this.resetInteractionPause();
            this.useSessionData();
        }

        if (this.currentTime > this.field.time.end && this.formsShown) {
            this.formsShown = false;
            this.formsShownChange.emit(this.formsShown);
        }

        if (field.type === OverlayType.Submit
      && field.time.end + this.formStopTime < this.currentTime
      && field.time.end > this.currentTime
      && this.videoState === VideoStateEnum.Playing
      && !this.formsShown
        ) {
            this.store.dispatch(SetVideoStateTypeAction({
                videoState: VideoStateEnum.Interaction
            }));
            this.formsShown = true;
            this.formsShownChange.emit(this.formsShown);
        }
    }

    useSessionData(): void {
        if (this.field.type === OverlayType.Submit) {
            return;
        }

        const fieldSessionData = {
        };
        fieldSessionData[this.field.uid] = this.sessionService.data.get(this.field.uid) || this.field.value;

        if (fieldSessionData[this.field.uid]) {
            this.setCheckboxValue(fieldSessionData[this.field.uid]);

            if (Object.keys(fieldSessionData).length !== 0) {
                this.formGroup.patchValue(fieldSessionData);
            }
        }
    }

    private resetInteractionPause() {
        this.formsShown = false;
        this.submitButtonClicked.emit(false);
    }

    public onInputFocusChange(isFocused: boolean, fieldType: string): void {
        const reportingId = this.field.reportingId || '';
        if (!isFocused && fieldType === 'input') {
            this.tracking.track(EventEnum.InputFocused, {
                reportingId,
                fieldLabel: this.field.label
            });
        }

        this.store.dispatch(ChangeSpaceKeyListenerAction({
            isSpaceKeyListenerOn: isFocused
        }));
        this.lockOrientationForActiveInput(!isFocused);

    }

    private lockOrientationForActiveInput(isInputActive: boolean): void {
        if (isInputActive) {
            (screen.orientation as any).lock(this.getCurrentOrientation()).catch();
        } else {
            screen.orientation.unlock();
        }
    }

    private getCurrentOrientation(): OrientationType {
        const {type}: ScreenOrientation = screen.orientation;
        return type.startsWith(this.PORTRAIT_ORIENTATION)
            ? this.PORTRAIT_ORIENTATION
            : this.LANDSCAPE_ORIENTATION;
    }

    onSelectionChange(selectedOption: any): void {
        const reportingId = this.field.reportingId || '';
        this.tracking.track(EventEnum.DropdownOptionSelected, {
            reportingId,
            fieldLabel: this.field.label,
            selectedOption
        });
    }

    initializeForms(): void {
        if (this.field.type === OverlayType.Submit) {
            return;
        }

        if (this.field.type === OverlayType.Checkbox) {
            this.formGroup.addControl(this.field.uid, new UntypedFormControl(false, []));
        } else {
            this.formGroup.addControl(this.field.uid,
                new UntypedFormControl((this.field.text && this.field.type === OverlayType.Input) ? this.field.text : '',
                    this.field.required ? [Validators.required] : []));
        }
    }

    onChangeCheckbox(): void {
        if (this.field.type === OverlayType.Checkbox) {
            this.isChecked = !this.isChecked;

            const reportingId = this.field.reportingId || '';
            this.tracking.track(EventEnum.CheckboxChanged, {
                reportingId,
                fieldLabel: this.field.label,
                checked: this.isChecked
            });

            this.setFieldControlValue(this.isChecked);
        }
        screen.orientation.unlock();
    }

    setFieldControlValue(value): void {
        const fieldObject = {
        };
        fieldObject[this.field.uid] = value;

        this.formGroup.patchValue(fieldObject);
    }

    private setCheckboxValue(value: boolean): void {
        if (this.field.type === OverlayType.Checkbox) {
            this.isChecked = value ? value : false;
        }
    }

    isSafari(): boolean {
        return this.browserService.isSafari();
    }

    calculateCheckboxSize() {
        const minimalCheckboxSize = 1.4285714285714286;
        let checkboxSize;

        if (this.field.font.size > minimalCheckboxSize) {
            checkboxSize = 0.8 + 'em';
        } else {
            checkboxSize = this.browserService.isMobileDevice() ? 11 + 'px' : 17 + 'px';
        }

        return checkboxSize;
    }
}
